import { logger } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";
import { capacitorStorage } from "@/lib/capacitor-storage";
import { Capacitor } from '@capacitor/core';

/**
 * Enhanced logout utility that ensures complete session cleanup
 */
export const performCompleteLogout = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    logger.debug("Starting complete logout process...");
    
    // Step 1: Sign out from Supabase
    const { error: signOutError } = await supabase.auth.signOut({
      scope: 'global'
    });
    
    if (signOutError) {
      logger.error("Supabase signOut error:", signOutError);
      return { success: false, error: signOutError.message };
    }

    // Step 2: Clear Capacitor storage (for native apps)
    if (Capacitor.isNativePlatform()) {
      try {
        const authKeys = ['supabase.auth.token', 'sb-ewdidxzgywdsvygvdcji-auth-token'];
        for (const key of authKeys) {
          await capacitorStorage.removeItem(key);
        }
        logger.debug("✅ Capacitor storage cleared");
      } catch (capacitorError) {
        logger.warn("Capacitor storage cleanup failed:", capacitorError);
      }
    }

    // Step 3: Clear localStorage of auth-related data (for web)
    try {
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.includes('supabase') || 
        key.includes('auth') ||
        key.includes('session') ||
        key.includes('user') ||
        key.includes('sb-')
      );
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          logger.warn(`Could not remove ${key} from localStorage:`, e);
        }
      });
    } catch (storageError) {
      logger.warn("localStorage cleanup failed:", storageError);
    }

    // Step 4: Clear sessionStorage as well
    try {
      const sessionKeys = Object.keys(sessionStorage).filter(key => 
        key.includes('supabase') || 
        key.includes('auth') ||
        key.includes('session') ||
        key.includes('user') ||
        key.includes('sb-')
      );
      sessionKeys.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          logger.warn(`Could not remove ${key} from sessionStorage:`, e);
        }
      });
    } catch (storageError) {
      logger.warn("sessionStorage cleanup failed:", storageError);
    }

    // Step 4b: Purge offline clinical caches so the next person to use this
    // shared device cannot read the previous worker's patient data.
    try {
      const { purgeClinicalOfflineCaches } = await import("@/lib/offline/clinicalCachePurge");
      await purgeClinicalOfflineCaches();
    } catch (purgeError) {
      logger.warn("Offline clinical cache purge failed:", purgeError);
    }

    logger.debug("Complete logout successful - forcing redirect");
    
    // Step 5: Force hard redirect to auth page
    setTimeout(() => {
      window.location.replace('/auth');
    }, 100);
    
    return { success: true };
    
  } catch (error) {
    logger.error("Unexpected error during complete logout:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Unexpected logout error" 
    };
  }
};

/**
 * Check if user is properly authenticated
 */
export const verifyAuthState = async (): Promise<{ isAuthenticated: boolean; user: any | null; session: any | null }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user ?? null;
    return {
      isAuthenticated: !!session && !!user,
      user,
      session
    };
  } catch (error) {
    logger.error("Error verifying auth state:", error);
    return {
      isAuthenticated: false,
      user: null,
      session: null
    };
  }
};